import { useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { jobService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";
import type { Job } from "../types/database";
import { format } from "date-fns";

type SortField =
  | "project_number"
  | "title"
  | "prep_date"
  | "ship_date"
  | "return_date";
type SortDirection = "asc" | "desc";

interface JobWithCheckoutStatus extends Job {
  checkoutStatus: "none" | "partial" | "complete";
  totalEquipment: number;
  checkedOutEquipment: number;
}

export default function JobSelection() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { currentOrganization } = useAuth();
  const [activeTab, setActiveTab] = useState<
    "all" | "upcoming" | "in_progress" | "completed"
  >("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [view, setView] = useState<"grid" | "list">("list");
  const [sortField, setSortField] = useState<SortField>("prep_date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // Fetch jobs data
  const {
    data: jobs = [],
    isLoading,
    error,
  } = useQuery<Job[]>({
    queryKey: ["jobs", currentOrganization?.id],
    queryFn: () => jobService.getJobs(currentOrganization?.id),
    enabled: !!currentOrganization?.id,
  });

  // Calculate checkout status for each job
  const jobsWithCheckoutStatus = useMemo((): JobWithCheckoutStatus[] => {
    return jobs.map((job) => {
      const equipment = job.equipment || [];
      const totalEquipment = equipment.length;

      // Count equipment that has been checked out
      const checkedOutEquipment = equipment.filter(() => {
        // Count assets that belong to this inventory item and are checked out
        const checkedOutAssets =
          job.assets?.filter(() => {
            // For now, we'll use a simple approach - if there are any assets checked out
            // and the job has equipment, we'll consider it partially checked out
            // In a real implementation, you'd need to fetch the actual assets to get their parent_item_id
            return true;
          }).length || 0;

        // If there are any checked out assets, consider this item checked out
        return checkedOutAssets > 0;
      }).length;

      let checkoutStatus: "none" | "partial" | "complete" = "none";
      if (totalEquipment === 0) {
        checkoutStatus = "none";
      } else if (job.assets && job.assets.length > 0) {
        // If there are any assets checked out, show as partial
        // In a real implementation, you'd check if all equipment items have their required assets
        checkoutStatus = "partial";
        // If all equipment items are checked out, show as complete
        if (checkedOutEquipment === totalEquipment) {
          checkoutStatus = "complete";
        }
      }

      return {
        ...job,
        checkoutStatus,
        totalEquipment,
        checkedOutEquipment: job.assets?.length || 0, // Use actual asset count for now
      };
    });
  }, [jobs]);

  // Filter jobs based on active tab and search term
  const filteredJobs = useMemo(() => {
    return jobsWithCheckoutStatus.filter((job) => {
      const matchesTab = activeTab === "all" || job.status === activeTab;
      const matchesSearch =
        job.project_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.location.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesTab && matchesSearch;
    });
  }, [jobsWithCheckoutStatus, activeTab, searchTerm]);

  // Sort jobs based on sortField and sortDirection
  const sortedJobs = useMemo(() => {
    return [...filteredJobs].sort((a, b) => {
      let aValue: string | number = a[sortField];
      let bValue: string | number = b[sortField];

      // Handle date fields
      if (sortField.includes("date")) {
        aValue = new Date(aValue as string).getTime();
        bValue = new Date(bValue as string).getTime();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredJobs, sortField, sortDirection]);

  // Handle sort click
  const handleSortClick = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd MMM yyyy");
  };

  // Render sort indicator
  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) return null;

    return (
      <span className="ml-1 inline-block">
        {sortDirection === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  // Get row background color based on checkout status
  const getRowBackgroundColor = (checkoutStatus: string) => {
    switch (checkoutStatus) {
      case "complete":
        return "bg-green-50 hover:bg-green-100";
      case "partial":
        return "bg-orange-50 hover:bg-orange-100";
      default:
        return "hover:bg-gray-50";
    }
  };

  // Get checkout status badge
  const getCheckoutStatusBadge = (job: JobWithCheckoutStatus) => {
    if (job.totalEquipment === 0) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
          No Equipment
        </span>
      );
    }

    switch (job.checkoutStatus) {
      case "complete":
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
            ✓ Complete
          </span>
        );
      case "partial":
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
            ⚠ In Progress
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
            Not Started
          </span>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">
          Error loading jobs. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Picklist</h1>
          <p className="mt-1 text-sm text-gray-500">
            Select a job to start scanning equipment
          </p>
        </div>
        <Link
          to={`/org/${orgId}/jobs`}
          className="text-primary hover:text-primary/80 font-medium"
        >
          ← Back to Jobs
        </Link>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("all")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "all"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            All
          </button>
          <button
            onClick={() => setActiveTab("upcoming")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "upcoming"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Upcoming
          </button>
          <button
            onClick={() => setActiveTab("in_progress")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "in_progress"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            In Progress
          </button>
          <button
            onClick={() => setActiveTab("completed")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "completed"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Completed
          </button>
        </nav>
      </div>

      {/* Search and View Toggle */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search jobs..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex border border-gray-300 rounded-lg overflow-hidden">
          <button
            className={`px-3 py-2 ${
              view === "grid"
                ? "bg-primary text-white"
                : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
            onClick={() => setView("grid")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
              />
            </svg>
          </button>
          <button
            className={`px-3 py-2 ${
              view === "list"
                ? "bg-primary text-white"
                : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
            onClick={() => setView("list")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 10h16M4 14h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Results count */}
      <div className="text-sm text-gray-500">
        Showing {sortedJobs.length} jobs
      </div>

      {/* Grid View */}
      {view === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {sortedJobs.map((job) => (
            <Link
              key={job.id}
              to={`/org/${orgId}/picklist/${job.id}`}
              className={`bg-white rounded-lg shadow hover:shadow-lg transition-shadow border border-gray-100 overflow-hidden flex flex-col ${
                job.checkoutStatus === "complete"
                  ? "ring-2 ring-green-200"
                  : job.checkoutStatus === "partial"
                  ? "ring-2 ring-orange-200"
                  : ""
              }`}
            >
              <div className="p-5">
                {/* Status Badge */}
                <div className="flex justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-600">
                    {job.project_number}
                  </span>
                  <span
                    className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                      job.status === "upcoming"
                        ? "bg-yellow-100 text-yellow-800"
                        : job.status === "in_progress"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    {job.status === "in_progress"
                      ? "In Progress"
                      : job.status.charAt(0).toUpperCase() +
                        job.status.slice(1)}
                  </span>
                </div>

                {/* Job Title */}
                <h3 className="font-semibold text-gray-900 text-lg mb-3">
                  {job.title}
                </h3>

                {/* Checkout Status */}
                <div className="mb-3">{getCheckoutStatusBadge(job)}</div>

                {/* Location */}
                <div className="mt-2 flex items-start text-sm">
                  <svg
                    className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span className="text-gray-600">{job.location}</span>
                </div>

                {/* Dates Section */}
                <div className="mt-4 pt-3 border-t border-gray-100">
                  {/* Prep Date */}
                  <div className="flex items-center text-sm">
                    <svg
                      className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="text-gray-700">
                      <span className="font-medium">Prep:</span>{" "}
                      {formatDate(job.prep_date)}
                    </span>
                  </div>

                  {/* Ship Date */}
                  <div className="mt-1.5 flex items-center text-sm">
                    <svg
                      className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-gray-700">
                      <span className="font-medium">Ship:</span>{" "}
                      {formatDate(job.ship_date)}
                    </span>
                  </div>

                  {/* Return Date */}
                  <div className="mt-1.5 flex items-center text-sm">
                    <svg
                      className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3"
                      />
                    </svg>
                    <span className="text-gray-700">
                      <span className="font-medium">Return:</span>{" "}
                      {formatDate(job.return_date)}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left bg-gray-50">
                <th
                  className="p-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortClick("project_number")}
                >
                  Project # {renderSortIndicator("project_number")}
                </th>
                <th
                  className="p-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortClick("title")}
                >
                  Project Name {renderSortIndicator("title")}
                </th>
                <th className="p-4">Checkout Status</th>
                <th
                  className="p-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortClick("prep_date")}
                >
                  Prep Date {renderSortIndicator("prep_date")}
                </th>
                <th
                  className="p-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortClick("ship_date")}
                >
                  Ship Date {renderSortIndicator("ship_date")}
                </th>
                <th
                  className="p-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortClick("return_date")}
                >
                  Return Date {renderSortIndicator("return_date")}
                </th>
                <th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedJobs.map((job) => {
                const detailUrl = `/org/${orgId}/picklist/${job.id}`;

                return (
                  <tr
                    key={job.id}
                    className={`border-t transition-colors cursor-pointer ${getRowBackgroundColor(
                      job.checkoutStatus
                    )}`}
                    onClick={() => navigate(detailUrl)}
                    onDoubleClick={() => navigate(detailUrl)}
                  >
                    <td className="p-4 font-medium">{job.project_number}</td>
                    <td className="p-4">{job.title}</td>
                    <td className="p-4">{getCheckoutStatusBadge(job)}</td>
                    <td className="p-4">{formatDate(job.prep_date)}</td>
                    <td className="p-4">{formatDate(job.ship_date)}</td>
                    <td className="p-4">{formatDate(job.return_date)}</td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex space-x-2">
                        <Link
                          to={detailUrl}
                          className="text-primary hover:underline"
                        >
                          Start Picklist
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
